import { defineStore } from 'pinia';
import { ref } from 'vue';

import notificationApi from '@/config/api/notification';
import { useClientApi } from '@/plugins/api/lk';
import { useSentry } from '@/plugins/sentry';
import type {
  NotificationsCategory,
  NotificationsData,
  ServerNotificationsData,
} from '@/types/Notifications';
import type { VuetifyDateInterval } from '@/types/utilities/DateTime';

import { useAuthStore } from './authStore';

import type { NotifyPlugin } from '@@/types/notifyPlugin';
import type { StateChanger } from 'vue-infinite-loading';

export interface GetNotificationsArgs {
  params?: NotificationsFilters;
  notify: NotifyPlugin;
}

export interface NotificationsFilters {
  date: { start?: string; end?: string } | null;
  category: NotificationsCategory | null;
}

const LIMIT = 20;

export const useNotificationsStore = defineStore('notifications', () => {
  const authStore = useAuthStore();
  const notifications = ref<NotificationsData>({
    list: [],
    total: 0,
  });

  const isLoading = ref(false);

  const currentFilters = ref<NotificationsFilters>({
    date: null,
    category: null,
  });

  const selectedCategory = ref<NotificationsCategory | null>(null);
  const selectedDate = ref<VuetifyDateInterval>([]);

  async function getNotifications (
    args?: GetNotificationsArgs,
  ): Promise<NotificationsData> {
    const params = args?.params ?? {
      date: null,
      category: null,
    };
    const notify = args?.notify;

    currentFilters.value = {
      date: params.date,
      category: params.category,
    };

    isLoading.value = true;

    try {
      const apiParams: Record<string, any> = {};

      if (params.category) {
        apiParams.category = params.category;
      }

      if (params.date?.start) {
        apiParams.date_from = `${ params.date.start }T00:00:00`;
      }

      if (params.date?.end) {
        apiParams.date_to = `${ params.date.end }T23:59:59`;
      }

      apiParams.limit = LIMIT;

      const url = notificationApi.notification.list;
      const { data } = await useClientApi().get<ServerNotificationsData>(url, {
        params: apiParams,
      });

      notifications.value.list = data.list;
      notifications.value.total = data.total;

      authStore.setHasUnreadNotifications(data.has_unread_notices);

      return notifications.value;
    } catch (error) {
      console.error(
        '🚀 ~ file: notifications.ts ~ getNotifications ~ error',
        error,
      );
      useSentry().captureException(error);
      if (notify) {
        notify.create({
          type: 'negative',
          message: 'Ошибка при получении уведомлений',
        });
      }
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function getNextPage ({ infiniteState, notify }: { infiniteState: StateChanger; notify: NotifyPlugin }): Promise<void> {
    try {
      if (notifications.value.list.length === 0) {
        infiniteState.complete();
        return;
      }

      if (notifications.value.list.length % LIMIT !== 0) {
        infiniteState.complete();
        return;
      }

      const lastNotification
        = notifications.value.list[notifications.value.list.length - 1];
      const lastId = lastNotification.id;

      const apiParams: Record<string, any> = {
        last_id: lastId,
        limit: LIMIT,
      };

      if (currentFilters.value.category) {
        apiParams.category = currentFilters.value.category;
      }

      if (currentFilters.value.date?.start) {
        apiParams.date_from = `${ currentFilters.value.date.start }T00:00:00`;
      }

      if (currentFilters.value.date?.end) {
        apiParams.date_to = `${ currentFilters.value.date.end }T23:59:59`;
      }

      const url = notificationApi.notification.list;
      const { data } = await useClientApi().get<ServerNotificationsData>(url, {
        params: apiParams,
      });

      if (data.list.length === 0) {
        infiniteState.complete();
        return;
      }

      notifications.value.list.push(...data.list);
      notifications.value.total = data.total;

      authStore.setHasUnreadNotifications(data.has_unread_notices);

      infiniteState.loaded();
    } catch (error) {
      console.error('🚀 ~ file: notifications.ts ~ getNextPage ~ error', error);
      useSentry().captureException(error);
      notify.create({
        type: 'negative',
        message: 'Ошибка при загрузке дополнительных уведомлений',
      });
      infiniteState.error();
    }
  }

  function resetFilters (): void {
    selectedCategory.value = null;
    selectedDate.value = [];
  }

  return {
    notifications,
    isLoading,
    currentFilters,
    selectedCategory,
    selectedDate,
    getNotifications,
    getNextPage,
    resetFilters,
  };
});
